import { createInertiaApp, router } from '@inertiajs/react'
import { createElement } from 'react'
import { createRoot } from 'react-dom/client'
import EmptyLayout from "@/components/Layout/EmptyLayout"
import { ThemeProvider } from "@/contexts/ThemeContext"
import { toast } from 'sonner'

declare global {
  interface Window {
    gtag?: (...args: unknown[]) => void
  }
}

// Handle CSRF token expiration (when browser sits idle for a long time)
// Rails returns 419 status when CSRF token is invalid
router.on('httpException', (event) => {
  const response = event.detail.response

  if (response.status === 419) {
    event.preventDefault()
    toast.error('Your session has expired. Refreshing page...', {
      duration: 2000,
    })
    setTimeout(() => {
      window.location.reload()
    }, 1500)
  }
})

createInertiaApp({
  // Set default page title
  title: title => title ? `${title} - Banshee.chat` : 'Banshee.chat',

  progress: {
    delay: 250,
    color: '#7fffe4', // ghost mint
    includeCSS: true,
    showSpinner: false,
  },

  resolve: (name) => {
    const pages = import.meta.glob('../pages/**/*.tsx', {eager: true})
    const page = pages[`../pages/${name}.tsx`] as any

    if (!page) {
      console.error(`Missing Inertia page component: '${name}.tsx'`)
    }

    // Only apply automatic layout wrapping if the page doesn't define its own
    page.default.layout = page.default.layout || ((pageElement: React.ReactElement) => {
      // Design page, Auth pages, Invitations pages, and Accounts/New use empty layout
      if (name === 'Design' || name.startsWith('Auth/') || name.startsWith('Invitations/') || name === 'Accounts/New') {
        return createElement(EmptyLayout, null, pageElement)
      }

      // Pages using MainLayout (Inbox, Conversation, etc.) handle their own layout
      // with the required props, so we just return the page element directly
      return pageElement
    })

    return page
  },

  setup({ el, App, props }) {
    if (el) {
      createRoot(el).render(
        createElement(ThemeProvider, null, createElement(App, props))
      )
    } else {
      console.error(
        'Missing root element.\n\n' +
          'If you see this error, it probably means you load Inertia.js on non-Inertia pages.\n' +
          'Consider moving <%= vite_typescript_tag "inertia" %> to the Inertia-specific layout instead.',
      )
    }

    if (process.env.NODE_ENV === 'development') {
      console.log('Inertia Page Props:', props.initialPage.props)
    }

    // Google Analytics: track SPA navigation
    const gaMeasurementId = props.initialPage.props.ga_measurement_id as string | undefined
    if (gaMeasurementId && window.gtag) {
      router.on('navigate', (event) => {
        window.gtag?.('config', gaMeasurementId, {
          page_path: event.detail.page.url,
        })
      })
    }
  },
})
